import React, { useRef, useState, useEffect } from "react";
import { Card, Button, Space, Modal, Image, Empty, Typography } from "antd";
import { CameraOutlined, UploadOutlined } from "@ant-design/icons";
import { uploadVisitPhoto } from "../network/api";
import { notify, serverError } from "../utils/notify";

const { Text } = Typography;

// D5 — capture or pick a photo and attach it to the visit. Uses getUserMedia for
// an in-app camera (rear-facing), with a native file-input fallback (which opens
// the camera on mobile and a picker on desktop / when getUserMedia is blocked).
export default function PhotoCapture({ visit, canEdit, onChange }) {
  const [camOpen, setCamOpen] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const photos = visit.photos || [];

  // Acquire/release the camera stream while the modal is open.
  useEffect(() => {
    if (!camOpen) return undefined;
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        setStreaming(true);
      } catch (e) {
        notify.warning("Camera unavailable — use “Choose photo” instead.");
        setCamOpen(false);
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setStreaming(false);
    };
  }, [camOpen]);

  const upload = async (blob) => {
    setUploading(true);
    try {
      await uploadVisitPhoto(visit._id, blob);
      notify.success("Photo added");
      onChange?.();
    } catch (e) {
      notify.error(serverError(e, "Failed to upload photo"));
    } finally {
      setUploading(false);
    }
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob(
      (blob) => {
        setCamOpen(false);
        if (blob) upload(blob);
      },
      "image/jpeg",
      0.85
    );
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  return (
    <Card
      title={`Photos (${photos.length})`}
      extra={
        canEdit && (
          <Space>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onFile} />
            <Button icon={<UploadOutlined />} onClick={() => fileRef.current?.click()} loading={uploading}>Choose photo</Button>
            <Button type="primary" icon={<CameraOutlined />} onClick={() => setCamOpen(true)} loading={uploading}>Take photo</Button>
          </Space>
        )
      }
    >
      {photos.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No photos" />
      ) : (
        <Image.PreviewGroup>
          <Space wrap>
            {photos.map((p, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <Image src={p.url} width={96} height={72} style={{ objectFit: "cover", borderRadius: 6 }} />
                {p.caption ? <div><Text type="secondary" style={{ fontSize: 11 }}>{p.caption}</Text></div> : null}
              </div>
            ))}
          </Space>
        </Image.PreviewGroup>
      )}

      <Modal open={camOpen} onCancel={() => setCamOpen(false)} footer={null} title="Take a photo" destroyOnClose>
        <video ref={videoRef} playsInline muted style={{ width: "100%", borderRadius: 8, background: "#000" }} />
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <Button type="primary" size="large" icon={<CameraOutlined />} disabled={!streaming || uploading} loading={uploading} onClick={capture}>
            Capture
          </Button>
        </div>
      </Modal>
    </Card>
  );
}
